import { useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useCustomMutation, useInvalidate, useUpdate } from '@refinedev/core'
import { Button } from '@ds/components'
import { CARD_COLORS } from '@ds/theme/color-mode'
import { formatApiDate, formatApiDay } from '@modules/api-core'
import type {
  TPublisherApprovalDraftReviewSong,
  TPublisherCopyrightDraftReviewSong,
  TPublisherSong,
} from '@modules/types'
import { TRequestEventName } from '@publisher/modules/analytics/channels'
import { useAnalytics } from '@publisher/modules/analytics/hooks'
import { getSubmitError } from '../../../utils/getSubmitError'
import { SONGS_LIST_STRINGS } from '../strings'
import type { TPublisherCopyrightRequestShowProps } from '../types'

type TCopyrightAnswer = { print: string; digital: string; notice: string; notOwned: boolean }

const PRINT_CLAIM = 1
const DIGITAL_CLAIM = 2

const toPercent = (share?: number | null) =>
  share === null || share === undefined ? '' : String(Math.round(share * 10000) / 100)

const toShare = (percent: string) => (percent === '' ? null : Number(percent) / 100)

const labelSx = { m: 0, mb: 0.5, fontSize: 13, color: CARD_COLORS.label } as const

const fieldSx = {
  font: 'inherit',
  fontSize: 16,
  lineHeight: 'normal',
  color: 'text.primary',
  border: 1,
  borderColor: CARD_COLORS.fieldBorder,
  borderRadius: 1,
  px: 1.75,
  py: 1.5,
  bgcolor: 'background.paper',
  boxSizing: 'border-box',
  width: '100%',
  '&:disabled': {
    bgcolor: CARD_COLORS.fieldDisabledBg,
    color: CARD_COLORS.muted,
    borderColor: CARD_COLORS.fieldDisabledBorder,
  },
} as const

const fieldLabelSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  fontSize: 14,
  color: 'text.secondary',
} as const

const SongTitle = ({ song }: { song: TPublisherSong }) => (
  <Box>
    <Typography sx={labelSx}>{song.clientRequestSong?.writers}</Typography>
    <Typography component="h3" sx={{ m: 0, fontSize: 24, fontWeight: 700 }}>
      {song.clientRequestSong?.title}
    </Typography>
  </Box>
)

const SongComment = ({ song }: { song: TPublisherSong }) =>
  song.clientRequestSong?.additionalComments ? (
    <Box>
      <Typography sx={labelSx}>{SONGS_LIST_STRINGS.comment}</Typography>
      <Typography sx={{ m: 0, fontSize: 15 }}>{song.clientRequestSong.additionalComments}</Typography>
    </Box>
  ) : null

const ShareField = ({
  label,
  value,
  disabled,
  onChange,
  onBlur,
}: {
  label: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
  onBlur: () => void
}) => (
  <Box component="label" sx={fieldLabelSx}>
    {label}
    <Box component="span" sx={{ position: 'relative', display: 'block' }}>
      <Box
        component="input"
        type="text"
        inputMode="decimal"
        value={value}
        disabled={disabled}
        onChange={event => {
          if (/^$|^(100|\d{1,2}(\.\d{0,2})?)$/.test(event.target.value)) {
            onChange(event.target.value)
          }
        }}
        onBlur={onBlur}
        sx={fieldSx}
      />
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          right: 14,
          top: '50%',
          transform: 'translateY(-50%)',
          color: CARD_COLORS.label,
          fontSize: 16,
        }}
      >
        %
      </Box>
    </Box>
  </Box>
)

export const PublisherCopyrightRequest = ({
  requestId,
  selectedRequest,
  initialLoading,
  apiUrl,
  onDrawerClose,
  onReviewSuccess,
  isRequestSubmitted,
}: TPublisherCopyrightRequestShowProps) => {
  const songs = selectedRequest.songs ?? []
  const asksPrint = selectedRequest.asksPrintShare === 1
  const asksDigital = selectedRequest.asksDigitalShare === 1
  const asksShares = asksPrint || asksDigital
  const asksNotice = selectedRequest.asksNotice === 1
  const region = selectedRequest.region && {
    territories: selectedRequest.region.territories.map(({ id, isExcluded }) => ({
      id,
      isExcluded,
    })),
    countries: selectedRequest.region.countries.map(({ id, isExcluded }) => ({ id, isExcluded })),
  }
  const draftSongs: Array<TPublisherApprovalDraftReviewSong | TPublisherCopyrightDraftReviewSong> =
    selectedRequest.draftReview?.songs ?? []
  const drafts = draftSongs.filter(
    (draft): draft is TPublisherCopyrightDraftReviewSong => 'printIsNotOwned' in draft,
  )
  const [answers, setAnswers] = useState<Record<number, TCopyrightAnswer>>(() =>
    Object.fromEntries(
      songs.map(song => {
        const draft = drafts.find(item => item.songId === song.id)
        return [
          song.id,
          {
            print: toPercent(draft ? draft.printPublisherShare : song.suggestedCopyrightSharePrint),
            digital: toPercent(
              draft ? draft.digitalPublisherShare : song.suggestedCopyrightShareDigital,
            ),
            notice: (draft ? draft.copyrightNotice : song.suggestedCopyrightNotice) ?? '',
            notOwned: draft?.printIsNotOwned === 1 || draft?.digitalIsNotOwned === 1,
          },
        ]
      }),
    ),
  )
  const [submitError, setSubmitError] = useState('')
  const { mutate: saveDraft } = useUpdate()
  const { mutate: sendReview, isPending } = useCustomMutation()
  const invalidate = useInvalidate()
  const { channels } = useAnalytics()

  const answered =
    isRequestSubmitted ||
    songs.some(song => (song.claimedShares?.length ?? 0) > 0 || Boolean(song.submittedCopyrightNotice))

  const toReviewSongs = (current: Record<number, TCopyrightAnswer>) =>
    songs.map(song => {
      const answer = current[song.id]
      return {
        songId: song.id,
        isApproved: null,
        copyrightNotice: answer.notOwned || answer.notice.trim() === '' ? null : answer.notice,
        printPublisherShare: answer.notOwned ? 0 : toShare(answer.print),
        digitalPublisherShare: answer.notOwned ? 0 : toShare(answer.digital),
        printIsNotOwned: answer.notOwned ? 1 : 0,
        digitalIsNotOwned: answer.notOwned ? 1 : 0,
        ...(region && { region }),
      }
    })

  const save = (current: Record<number, TCopyrightAnswer>) =>
    saveDraft({
      resource: 'publisher-request',
      id: requestId,
      values: { draftReview: { songs: toReviewSongs(current) } },
      meta: { method: 'PATCH', disableNotification: true },
    })

  const answerSong = (songId: number, change: Partial<TCopyrightAnswer>, persist = false) => {
    const next = { ...answers, [songId]: { ...answers[songId], ...change } }
    setAnswers(next)
    if (persist) save(next)
  }

  const songAsks = (song: TPublisherSong) => song.asksShares !== 0
  const isAnswered = (song: TPublisherSong, answer: TCopyrightAnswer) =>
    answer.notOwned ||
    ((!asksPrint || !songAsks(song) || answer.print !== '') &&
      (!asksDigital || !songAsks(song) || answer.digital !== '') &&
      (!asksNotice || answer.notice.trim() !== ''))
  const answeredCount = songs.filter(song => isAnswered(song, answers[song.id])).length

  const confirm = () => {
    channels.request.send({ event: TRequestEventName.Review, requestId })
    setSubmitError('')
    sendReview(
      {
        url: `${apiUrl}/publisher-request/${requestId}/review`,
        method: 'post',
        values: {
          answerReceivedOn: formatApiDay(new Date().toISOString()),
          songs: toReviewSongs(answers),
        },
      },
      {
        onSuccess: () => {
          onDrawerClose()
          invalidate({ resource: 'publisher-request', id: requestId, invalidates: ['list'] })
          onReviewSuccess()
        },
        onError: error =>
          setSubmitError(
            getSubmitError(
              error.message,
              songs.map(song => ({
                title: song.clientRequestSong?.title ?? '',
                writers: song.clientRequestSong?.writers ?? '',
              })),
            ).formattedMessage,
          ),
      },
    )
  }

  const answeredAt = songs
    .flatMap(song => [
      ...(song.claimedShares ?? []).map(share => share.createdAt),
      song.submittedCopyrightNotice?.createdAt ?? '',
    ])
    .sort()
    .pop()

  return (
    <Stack gap={2.75} sx={{ flex: 1, px: 4, pt: 2.75, pb: 4, bgcolor: 'background.paper' }}>
      {answered && (
        <Stack
          role="status"
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{
            bgcolor: CARD_COLORS.answeredBg,
            color: CARD_COLORS.answeredText,
            px: 2.5,
            py: 2,
            fontSize: 15,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 20, color: CARD_COLORS.paid }} />
          {SONGS_LIST_STRINGS.answeredOn(
            answeredAt ? formatApiDate(answeredAt) : '',
          )}
        </Stack>
      )}

      {songs.map(song => {
        const shares = song.claimedShares ?? []
        const notice = song.submittedCopyrightNotice?.copyrightNotice
        if (answered) {
          const notOwned = shares.length > 0 && shares.every(share => share.isNotOwned === 1)
          const owned = [
            [SONGS_LIST_STRINGS.printShare, shares.find(share => share.claimType === PRINT_CLAIM)],
            [
              SONGS_LIST_STRINGS.digitalShare,
              shares.find(share => share.claimType === DIGITAL_CLAIM),
            ],
          ] as const
          return (
            <Stack
              component="article"
              key={song.id}
              gap={2}
              sx={{ bgcolor: 'background.default', borderRadius: 1, px: 3.5, py: 3 }}
            >
              <Stack
                direction="row"
                flexWrap="wrap"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={2}
              >
                <SongTitle song={song} />
                {notOwned && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      minHeight: 36,
                      px: 2,
                      borderRadius: 999,
                      border: 1,
                      borderColor: CARD_COLORS.muted,
                      color: CARD_COLORS.inkSoft,
                      fontSize: 14,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {SONGS_LIST_STRINGS.notOwned}
                  </Box>
                )}
              </Stack>
              <SongComment song={song} />
              {!notOwned && owned.some(([, share]) => share) && (
                <Stack direction="row" flexWrap="wrap" gap={6}>
                  {owned.map(
                    ([label, share]) =>
                      share && (
                        <Box key={label}>
                          <Typography sx={labelSx}>{label}</Typography>
                          <Typography sx={{ m: 0, fontSize: 26, fontWeight: 700 }}>
                            {toPercent(share.share)}%
                          </Typography>
                        </Box>
                      ),
                  )}
                </Stack>
              )}
              {notice && (
                <Box>
                  <Typography sx={labelSx}>{SONGS_LIST_STRINGS.copyrightNotice}</Typography>
                  <Typography sx={{ m: 0, fontSize: 17 }}>{notice}</Typography>
                </Box>
              )}
            </Stack>
          )
        }

        const answer = answers[song.id]
        const locked = answer.notOwned || initialLoading || isPending
        return (
          <Stack
            component="article"
            key={song.id}
            gap={2.25}
            sx={{ bgcolor: 'background.default', borderRadius: 1, px: 3.5, py: 3 }}
          >
            <SongTitle song={song} />
            <SongComment song={song} />
            {asksShares && songAsks(song) && (
              <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                {asksPrint && (
                  <ShareField
                    label={SONGS_LIST_STRINGS.printShare}
                    value={answer.notOwned ? '' : answer.print}
                    disabled={locked}
                    onChange={print => answerSong(song.id, { print })}
                    onBlur={() => save(answers)}
                  />
                )}
                {asksDigital && (
                  <ShareField
                    label={SONGS_LIST_STRINGS.digitalShare}
                    value={answer.notOwned ? '' : answer.digital}
                    disabled={locked}
                    onChange={digital => answerSong(song.id, { digital })}
                    onBlur={() => save(answers)}
                  />
                )}
              </Box>
            )}
            <Box component="label" sx={fieldLabelSx}>
              {SONGS_LIST_STRINGS.copyrightNotice}
              <Box
                component="textarea"
                rows={2}
                value={answer.notOwned ? '' : answer.notice}
                disabled={locked}
                placeholder={answer.notOwned ? undefined : SONGS_LIST_STRINGS.copyrightPlaceholder}
                onChange={event => answerSong(song.id, { notice: event.target.value })}
                onBlur={() => save(answers)}
                sx={{ ...fieldSx, resize: 'vertical' }}
              />
            </Box>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minHeight: 44,
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              <Box
                component="input"
                type="checkbox"
                checked={answer.notOwned}
                disabled={initialLoading || isPending}
                onChange={event => answerSong(song.id, { notOwned: event.target.checked }, true)}
                sx={{ width: 20, height: 20, m: 0.25 }}
              />
              {SONGS_LIST_STRINGS.notOwned}
            </Box>
          </Stack>
        )
      })}

      {!answered && (
        <Stack alignItems="center" gap={1.5} sx={{ mt: 'auto', pt: 1.5 }}>
          <Typography sx={{ m: 0, fontSize: 15, color: 'text.secondary' }}>
            {SONGS_LIST_STRINGS.answeredCount(answeredCount, songs.length)}
          </Typography>
          {submitError && (
            <Typography role="alert" sx={{ m: 0, fontSize: 14, color: 'error.main' }}>
              {submitError}
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={confirm}
            disabled={initialLoading || songs.length === 0 || answeredCount < songs.length}
            loading={isPending}
            sx={{ minHeight: 52, fontSize: 16, fontWeight: 600 }}
          >
            {SONGS_LIST_STRINGS.sendReview}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
